/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Delete Payment Record button
 */
import { Button, ButtonProps, App } from 'antd';

import useDeletePaymentRecord from 'hooks/payments/mutate/useDeletePaymentRecord';
import { MODAL_TYPES } from 'components/widgets/modal';
import { formatAmount } from 'utils/accounts/allAccounts';

type IProps = {
	id: string;
	refundAmount: number;
	isDelete?: boolean;
};

const getModelProps = ({ isDelete, refundAmount }: { isDelete: boolean; refundAmount: number }) => {
	if (isDelete) {
		return {
			title: 'Delete Payment Record',
			content: (
				<>
					{'Are you sure you want to permanently delete this payment record?'}
					<br />
					<br />
					{'This action cannot be undone.'}
				</>
			),
			okText: 'Delete',
			okButtonProps: { danger: true },
		};
	}
	if (refundAmount > 0) {
		return {
			title: 'Confirm Payment Deallocation',
			content: (
				<>
					{`A refund of ${formatAmount(refundAmount)} exists for this payment.`}
					<br />
					{'If you proceed:'}
					<ul className='my-1'>
						<li>{'The recorded refund will be removed'}</li>
						<li>{'All associated claim write-offs will be reversed'}</li>
					</ul>
					{'This action cannot be undone.'}
				</>
			),
			okText: 'Deallocate',
			okButtonProps: { danger: true },
		};
	}
	return {
		title: 'Deallocate Payment Record',
		content: 'Are you sure you want to deallocate this payment record?',
		okText: 'Deallocate',
		okButtonProps: { danger: true },
	};
};
/**
 * Delete Payment Record button
 */
const DeletePaymentRecord: React.FC<IProps & ButtonProps> = ({ id, refundAmount, isDelete = false, ...restProps }) => {
	const { modal } = App.useApp();
	const { mutateAsync } = useDeletePaymentRecord(id, isDelete);
	const handleDelete = () => {
		modal.confirm({
			...MODAL_TYPES.confirm,
			width: 560,
			...getModelProps({ isDelete, refundAmount }),
			onOk: async () => {
				await mutateAsync();
			},
		});
	};
	return (
		<Button danger onClick={handleDelete} {...restProps}>
			{isDelete ? 'Delete' : 'Deallocate'}
		</Button>
	);
};

export default DeletePaymentRecord;
